import React, { useState, useEffect } from 'react'
import {
  Modal,
  Table,
  Badge,
  Button,
  Spinner,
  InputGroup,
  Form
} from 'react-bootstrap'
import { DebounceInput } from 'react-debounce-input'
import Pagination from '../../../../GlobalModules/Utility/Components/Pagination'
import Utility from '../../../../GlobalModules/Utility/Utility'
import ModalGlobal from '../../../../GlobalModules/Utility/Components/ModalGlobal'
import ErrorBoundary from '../../../../GlobalModules/Utility/Components/ErrorBoundary'
import ConfirmationAlert from '../../../../GlobalModules/Utility/Components/ConfirmationAlert'
import * as QuotationController from '../Controller/QuotationController'
import { connect } from 'react-redux'
import { withNamespaces } from 'react-i18next'
import { faRedo } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { loaderStateTrue, loaderStateFalse, handleActiveLink } from '../../../../Actions/AllAction';

const NotificationHistoryModal = ({ show, onHide, referenceId, storeId, quotationStatus, quotationDueDate, loaderStateTrue,
  loaderStateFalse, props }) => {
  const isPendingDueDateExpired = quotationStatus === 1 && quotationDueDate && new Date(quotationDueDate) < new Date(new Date().setHours(0, 0, 0, 0));
  const hideActions = quotationStatus === 0 || quotationStatus === 3 || quotationStatus === 4 || isPendingDueDateExpired;
  const [notificationHistory, setNotificationHistory] = useState([])
  const [notificationSearchTerm, setNotificationSearchTerm] = useState('')
  const [notificationCurrentPage, setNotificationCurrentPage] = useState(1)
  const [notificationItemsPerPage, setNotificationItemsPerPage] = useState(10)
  const [notificationTotalItems, setNotificationTotalItems] = useState(0)
  const [resendingNotificationId, setResendingNotificationId] = useState(null)
  const [showResendModal, setShowResendModal] = useState(false)
  const [pendingResendSlack, setPendingResendSlack] = useState('')

  const fetchNotificationHistory = async () => {
    try {
      const resp = await QuotationController.getQuotationNotificationHistoryApi(
        referenceId,
        notificationSearchTerm,
        notificationCurrentPage,
        notificationItemsPerPage,
        storeId
      )
      if (resp?.success) {
        setNotificationHistory(resp.data || [])
        setNotificationTotalItems(resp.total || 0)
      }
    } catch (error) {
      console.error('Error fetching notification history:', error)
    }
  }

  const confirmationModalForResend = (notificationSlack) => {
    setPendingResendSlack(notificationSlack)
    setShowResendModal(true)
  }

  const closeResendModal = () => {
    setShowResendModal(false)
    setPendingResendSlack('')
  }

  const confirmResendModal = async () => {
    await handleResendHistoryNotification(pendingResendSlack)
    closeResendModal()
  }

  const handleResendHistoryNotification = async (notificationSlack) => {
    try {
      setResendingNotificationId(notificationSlack)
      loaderStateTrue();

      const resp = await QuotationController.resendQuotationNotificationApi(notificationSlack)
      if (resp?.success) {
        Utility.toastNotifications('Notification resent successfully', 'Success', 'success')
        fetchNotificationHistory()
      } else {
        Utility.toastNotifications(resp?.message || 'Failed to resend notification', 'Error', 'error')
      }
    } catch (error) {
      Utility.toastNotifications('Failed to resend notification', 'Error', 'error')
    } finally {
      setResendingNotificationId(null)
      loaderStateFalse();
    }
  }

  const handleNotificationSearch = (e) => {
    const value = e.target.value || ''
    setNotificationSearchTerm(value)
    setNotificationCurrentPage(1)
  }

  const handleNotificationPageChange = (page) => {
    setNotificationCurrentPage(page)
  }

  const handleNotificationLimitChange = (limit) => {
    setNotificationItemsPerPage(limit)
    setNotificationCurrentPage(1)
  }

  useEffect(() => {
    if (show && referenceId) {
      fetchNotificationHistory()
    }
  }, [notificationSearchTerm, notificationCurrentPage, notificationItemsPerPage, show, referenceId])

  // Check multiple permissions at once
  const quotationViewPermissions = Utility.hasMultipleActionPermissions(
    'sales_and_orders',
    'quotations',
    ['view', 'create', 'edit', 'delete', 'export', 'approve', 'receive', 'generate_invoice', 'change_status', 'accept', 'reject', 'send_notification', 'notification_history'],
    props?.userCredentials?.user_details?.user_type || ['admin', 'app_admin']
  );

  /**
   * Identifies the latest (first match in array) records for targeted channels.
   * This handles scenarios where the latest Email might be at index 0, 
   * but the latest WhatsApp message might be at index 2.
   */
  const getLatestNotificationIdsByChannel = () => {
    const latestIds = {};
    
    for (const history of notificationHistory) {
      const channelKey = String(history?.channel || '').trim().toUpperCase();
      
      // If we haven't logged a unique ID for this channel yet, log it (since API lists newest first)
      if (channelKey && !latestIds[channelKey] && history?.slack) {
        latestIds[channelKey] = history.slack;
      }
    }
    return latestIds;
  };

  const latestChannelIdsMap = getLatestNotificationIdsByChannel();

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="lg" 
      centered 
      dialogClassName="modal-90w"
      className='quotation-notification-history-modal'
    >
      <Modal.Header closeButton className="border-bottom pb-3">
        <Modal.Title className="fw-semibold">Notification History</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        <div className="mb-4">
          <InputGroup className="shadow-sm">
            <DebounceInput
              element={Form.Control}
              placeholder="Search notifications by Recipient, Event Type, Status..."
              value={notificationSearchTerm}
              onChange={handleNotificationSearch}
              debounceTimeout={500}
              minLength={2}
              className="border rounded"
            />
          </InputGroup>
        </div>
        {notificationHistory.length > 0 ? (
          <div className="table-responsive">
            <Table hover className="align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th className="fw-semibold text-muted small text-uppercase" style={{ fontSize: '0.75rem' }}>Event Type</th>
                  <th className="fw-semibold text-muted small text-uppercase" style={{ fontSize: '0.75rem' }}>Channel</th>
                  <th className="fw-semibold text-muted small text-uppercase" style={{ fontSize: '0.75rem' }}>Recipient</th>
                  <th className="fw-semibold text-muted small text-uppercase" style={{ fontSize: '0.75rem' }}>Status</th>
                  <th className="fw-semibold text-muted small text-uppercase" style={{ fontSize: '0.75rem' }}>Sent At</th>
                  <th className="fw-semibold text-muted small text-uppercase" style={{ fontSize: '0.75rem' }}>Delivered At</th>
                  {!hideActions && (
                    <th className="fw-semibold text-muted small text-uppercase" style={{ fontSize: '0.75rem' }}>Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {notificationHistory.map((history, index) => {
                  const currentChannel = String(history?.channel || '').trim().toUpperCase();
                  
                  // Rule: Display resend only if it's the latest entry mapped for that distinct channel type
                  const isLatestForChannel = latestChannelIdsMap[currentChannel] === history?.slack;
                  const canResend = quotationViewPermissions.notification_history && isLatestForChannel;

                  return (
                    <tr key={index} style={{ verticalAlign: 'middle' }}>
                      <td>
                        <span className="fw-medium text-dark">{history.event_type || '-'}</span>
                      </td>
                      <td>
                        <Badge bg="info" className="fw-normal" style={{ fontSize: '0.75rem' }}>
                          {history.channel || '-'}
                        </Badge>
                      </td>
                      <td>
                        {history.recipient_address ? (
                          <div className="d-flex flex-wrap gap-2">
                            {history.recipient_address.split(',').map((email, i) => (
                              <Badge 
                                key={i} 
                                bg="light" 
                                text="dark" 
                                className="border border-secondary-subtle"
                                style={{ fontSize: '0.7rem', padding: '4px 8px', fontWeight: '500' }}
                              >
                                {email.trim()}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <div>
                          <Badge 
                            bg={history.status === 'SENT' ? 'success' : 'danger'} 
                            className="fw-normal"
                            style={{ fontSize: '0.75rem' }}
                          >
                            {history.status || '-'}
                          </Badge>
                          {history.error_message && (
                            <div className="text-danger small mt-1" style={{ fontSize: '0.7rem' }}>
                              {history.error_message}
                            </div>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="text-muted small">{history?.sent_at ? Utility.get12HourFormat(history.sent_at) : '-'}</span>
                      </td>
                      <td>
                        <span className="text-muted small">{history?.delivered_at ? Utility.get12HourFormat(history.delivered_at) : '-'}</span>
                      </td>
                      {!hideActions && (
                        <td>
                          {canResend ? (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => confirmationModalForResend(history.slack)}
                              disabled={resendingNotificationId === history.slack}
                              className="fw-medium"
                              style={{ fontSize: '0.8rem' }}
                            >
                              {resendingNotificationId === history.slack ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <>
                                  <FontAwesomeIcon icon={faRedo} className="me-1" />
                                  Resend
                                </>
                              )}
                            </Button>
                          ) : (
                            <span className="text-muted small italic">-</span>
                          )}
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-5">
            <div className="text-muted mb-2">
              <i className="fas fa-bell-slash fa-3x opacity-25"></i>
            </div>
            <p className="text-muted mb-0">No notification history found</p>
          </div>
        )}
        {notificationTotalItems > 0 && (
          <div className="mt-4 pt-3 border-top">
            <Pagination
              currentPage={notificationCurrentPage}
              itemsPerPage={notificationItemsPerPage}
              totalItems={notificationTotalItems}
              onPageChange={handleNotificationPageChange}
              onLimitChange={handleNotificationLimitChange}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="border-top pt-3">
        <Button 
          variant="secondary" 
          onClick={onHide}
          className="px-4"
        >
          Close
        </Button>
      </Modal.Footer>

      <ErrorBoundary title="ModalGlobal Error">
        <ModalGlobal
          show={showResendModal}
          onHide={closeResendModal}
          className="modalcustomize confirmationalertmodalDialog"
          bodyClassName="cancelConfirmationbody"
          headerclassName="close_btn_icon"
          title="Confirm Resend Notification"
          footer={false}
          body={
            <>
              <ErrorBoundary title="ConfirmationAlert Error">
                <ConfirmationAlert
                  BodyFirstContent="Are you sure you want to resend this notification?"
                  BodySecondContent=""
                  BodyThirdContent=""
                  confirmationButtonContent="Yes"
                  cancelButtonContent="No"
                  deleteConfirmButton={confirmResendModal}
                  deleteCancleButton={closeResendModal}
                />
              </ErrorBoundary>
            </>
          }
        />
      </ErrorBoundary>
    </Modal>
  )
}

const mapStateToProps = (globalState) => ({
  userCredentials: globalState.LoginReducer.userCredentials,
  token: globalState.LoginReducer.token,
  userEntryType: globalState.LoginReducer.userEntryType
})

export default connect(mapStateToProps, { loaderStateTrue, loaderStateFalse })(withNamespaces()(NotificationHistoryModal))